import { useEffect, useRef, useState } from 'react';
import { Card, Icon } from '@/components/kit';

/** Longest side of a stored item photo — it rides along with every shelf row. */
const MAX_SIDE = 320;

/** Shrink a picked photo to a small JPEG (the server takes at most 150 KB). */
export async function downsize(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bmp.width * scale));
  canvas.height = Math.max(1, Math.round(bmp.height * scale));
  canvas.getContext('2d')?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  bmp.close();
  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.82));
  if (!blob) throw new Error('The photo could not be read');
  return blob;
}

/**
 * `g_newitem` — the photo drop. The picture is kept until the item is saved
 * (there is no row to hang it on yet) and goes up right after.
 */
export function PhotoDrop({ onChange }: { onChange: (photo: Blob | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    try {
      const blob = await downsize(file);
      setError(null);
      setPreview(URL.createObjectURL(blob));
      onChange(blob);
    } catch {
      setError('That file is not a picture this browser can read.');
    }
  };

  return (
    <Card>
      <div className="flex" style={{ gap: 12, alignItems: 'center' }}>
        <button
          type="button"
          aria-label="Add a photo"
          onClick={() => input.current?.click()}
          style={{ width: 72, height: 72, borderRadius: 12, border: '1px dashed var(--color-border-default)', background: 'transparent', cursor: 'pointer', overflow: 'hidden', padding: 0 }}
        >
          {preview ? <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Icon name="camera" className="ic-lg" />}
        </button>
        <div className="grow">
          <p className="t-strong">Photo</p>
          <p className="t-cap mut mt4">{error ?? 'so the person at the shelf picks the right box'}</p>
        </div>
        {preview ? (
          <button type="button" className="dchip dchip-outline" onClick={() => { setPreview(null); onChange(null); }}>
            Remove
          </button>
        ) : (
          <button type="button" className="dchip dchip-outline" onClick={() => input.current?.click()}>
            Add photo
          </button>
        )}
      </div>
      <input ref={input} type="file" accept="image/*" hidden aria-label="Item photo" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
    </Card>
  );
}
